import { AuthBottomBlock } from "modules/auth/layout/AuthBottomBlock";
import { useI18n } from "i18n";
import { useAppDispatch } from "store/hooks";
import { ChangeEvent, FormEvent, useCallback, useMemo, useState } from "react";
import { useHistory } from "react-router";
import useQueryParams from "modules/queryParams/hooks/useQueryParams";
import useNotificationActions from "modules/notification/hooks/useNotificationActions";
import { getRemoHosts } from "config";
import { PageLoader } from "@remo-co/ui-core/src/components/PageLoader";
import { useSelector } from "react-redux";
import { selectMagicLinkEmail } from "modules/auth/redux/selectors";
import { CardContent } from "@remo-co/ui-core/src/components/CardContent";
import { Input } from "@remo-co/ui-core/src/components/Input";
import { TCAndPrivacyPolicyDisclaimer } from "modules/branding/TCAndPrivacyPolicyDisclaimer";
import { CardActions } from "@remo-co/ui-core/src/components/CardActions";
import { Button } from "@remo-co/ui-core/src/components/Button";
import sendSignInLinkToEmail from "modules/auth/utils/sendSignInLinkToEmail";
import {
  CHECK_EMAIL_TO_LOGIN_MODES,
  forbiddenEmailDomainsForTrial,
  trialFirstNameKey,
  trialLastNameKey,
  trialPhoneKey,
} from "modules/auth/constants";
import isValidEmailAddress from "@remo-co/email/src/isValid";
import { setMagicLinkEmail } from "modules/auth/redux/authSlice";
import { Box } from "@remo-co/ui-core/src/components/Box";
import {
  PhoneNumber,
  PhoneInput,
} from "@remo-co/ui-core/src/components/PhoneInput";
import { checkValidity, getRawValue } from "react-phone-hooks";
import { useStyles } from "./styles";

export const SignUpViewTrial = () => {
  const { t } = useI18n(["auth", "common"]);
  const dispatch = useAppDispatch();
  const [isLoading, setIsLoading] = useState(false);
  const { push } = useHistory();
  const styles = useStyles();
  const { redirectTo } = useQueryParams();
  const { addErrorNotification } = useNotificationActions();
  const remoUrls = getRemoHosts();
  const prefilledMagicLinkEmail = useSelector(selectMagicLinkEmail);
  const [email, setEmail] = useState(prefilledMagicLinkEmail ?? "");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [phoneNumber, setPhoneNumber] = useState({} as PhoneNumber);

  const phoneNumberError = useMemo(
    () => !(getRawValue(phoneNumber).length > 0 && checkValidity(phoneNumber)),
    [phoneNumber],
  );

  const onSubmit = useCallback(
    async (event: FormEvent) => {
      event.preventDefault();

      if (!email) {
        return;
      }

      if (!isValidEmailAddress(email)) {
        addErrorNotification({
          message: t("auth:enter.a.valid.business.email.address"),
          autoDismiss: 10,
        });
        return;
      }

      setIsLoading(true);

      try {
        await sendSignInLinkToEmail(
          email,
          redirectTo ? redirectTo.toString() : undefined,
          !remoUrls.includes(window.location.host)
            ? window.location.host
            : undefined,
        );

        dispatch(setMagicLinkEmail(email));

        localStorage.setItem(trialFirstNameKey, firstName);
        localStorage.setItem(trialLastNameKey, lastName);
        localStorage.setItem(trialPhoneKey, getRawValue(phoneNumber));

        push(`/check-email-to-login?mode=${CHECK_EMAIL_TO_LOGIN_MODES.TRIAL}`);
      } catch (error) {
        if (error instanceof Error) {
          addErrorNotification({
            message: t("auth:magic.link.invalid"),
            autoDismiss: 10,
          });
        }
      }
      setIsLoading(false);
    },
    [
      addErrorNotification,
      dispatch,
      email,
      firstName,
      lastName,
      push,
      redirectTo,
      remoUrls,
      t,
      phoneNumber,
    ],
  );

  const handleEmailChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      setEmail(event.target.value);
    },
    [],
  );

  const handleFirstNameChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      setFirstName(event.target.value);
    },
    [],
  );
  const handleLastNameChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      setLastName(event.target.value);
    },
    [],
  );

  const emailError = useMemo(() => {
    if (!email) return false;

    if (
      forbiddenEmailDomainsForTrial.find((domain) => email.includes(domain))
    ) {
      return t("auth:enter.a.valid.business.email.address");
    }

    return false;
  }, [email, t]);

  if (isLoading) {
    return (
      <div className={styles.loaderContainer}>
        <PageLoader message={t("wait")} data-testid="loading" />
      </div>
    );
  }

  return (
    <AuthBottomBlock>
      <div>
        <form
          noValidate
          onSubmit={onSubmit}
          data-testid="magic-link-login-form"
        >
          <CardContent className={styles.cardContent}>
            <Box className={styles.inputContainer}>
              <Box display="flex" gap={2} marginBottom={1}>
                <Input
                  name="firstName"
                  fullWidth
                  required
                  autoFocus
                  onChange={handleFirstNameChange}
                  label={t("common:first.name")}
                  size="sm"
                  value={firstName}
                  inputProps={{ "data-testid": "first-name" }}
                />
                <Input
                  name="lastName"
                  fullWidth
                  required
                  onChange={handleLastNameChange}
                  label={t("common:last.name")}
                  size="sm"
                  value={lastName}
                  inputProps={{ "data-testid": "last-name" }}
                />
              </Box>
              <Box display="flex" marginBottom={1}>
                <Input
                  id="email"
                  name="email"
                  fullWidth
                  required
                  error={emailError}
                  onChange={handleEmailChange}
                  label={t("auth:business.email.address")}
                  size="sm"
                  value={email}
                  inputProps={{ "data-testid": "email-input" }}
                />
              </Box>
              <PhoneInput
                name="phone"
                fullWidth
                required
                onChange={(val: PhoneNumber) => setPhoneNumber(val)}
                label={t("common:phone.number")}
                value={phoneNumber}
                size="small"
                inputProps={{ "data-testid": "phone-input" }}
                error={
                  getRawValue(phoneNumber).length === 0
                    ? false
                    : phoneNumberError && t("common:invalid.phone.number")
                }
                enableArrow
              />
            </Box>
            <Box marginTop={1} marginBottom={1}>
              <TCAndPrivacyPolicyDisclaimer />
            </Box>
          </CardContent>
          <CardActions>
            <Button
              data-testid="login-button"
              variant="primary"
              color="blue"
              fullWidth
              size="lg"
              type="submit"
              disabled={
                !email ||
                !firstName ||
                !lastName ||
                !!emailError ||
                !!phoneNumberError
              }
            >
              {t("auth:signup")}
            </Button>
          </CardActions>
        </form>
      </div>
    </AuthBottomBlock>
  );
};
